// Zoom state store — manages card zoom overlay
// Uses the same card DOM element, animated from slot to viewport center

import { getCardImageUrl, getCardBackImageUrl } from '../services/card-utils.js';
import { FLIPPABLE_LAYOUTS, SIDEWAYS_LAYOUTS, MD_BREAKPOINT } from '../logic/config.js';
import { readDuration } from '../logic/motion.js';

// Animation constants — durations come from CSS tokens (see logic/motion.js).
// Read lazily via getters so a media-query or reduced-motion change is picked
// up without a reload.
const EASING_SMOOTH = 'cubic-bezier(0.25, 0.8, 0.25, 1)';
const getZoomDurationMs = () => readDuration('--dur-zoom');
const getDfcFlipDurationMs = () => readDuration('--dur-dfc-flip');

export const zoomState = $state({
  active: false,
  closing: false, // true when zoom-out animation is in progress
  cardInfo: null,
  cardElement: null, // The .card-slot DOM element
  slotRect: null, // DOMRect captured before zoom
  isDfc: false,
  showingBack: false, // controls which image is displayed
  dfcRotation: 0, // wrapper rotateY (0 or 180), drives the CSS transition
  frontImageUrl: null,
  backImageUrl: null,
  isSideways: false, // split/planar/battle layout rotation (front face)
  isSidewaysBack: false, // back face sideways rotation
  tiltEnabled: false,
  isFlipping: false,
  isAnimating: false, // true during zoom-in/out animation
});

/**
 * Check if a card data object represents a battle card
 */
function isBattleCard(cardData) {
  return cardData?.type_line?.toLowerCase().includes('battle') ?? false;
}

/**
 * Determine if card should be rotated 90 degrees (desktop only)
 */
function determineCardRotation(cardData) {
  const isMobile = window.innerWidth < MD_BREAKPOINT;
  if (isMobile) return false;

  if (cardData.layout && SIDEWAYS_LAYOUTS.includes(cardData.layout)) {
    return true;
  }
  return isBattleCard(cardData);
}

/**
 * Determine if a DFC back face should be sideways.
 * Battle back faces are usually creatures (not sideways).
 */
function determineBackFaceRotation(cardData) {
  if (!cardData?.card_faces?.[1]) return false;
  const isMobile = window.innerWidth < MD_BREAKPOINT;
  if (isMobile) return false;

  const backFace = cardData.card_faces[1];
  // Back face is sideways only if it's also a sideways layout type
  if (backFace.type_line?.toLowerCase().includes('battle')) return true;
  // For split/flip layouts the whole card is sideways on both sides
  if (cardData.layout && SIDEWAYS_LAYOUTS.includes(cardData.layout)) return true;
  return false;
}

/**
 * Check if a card is double-faced
 */
function isDfcCard(cardData) {
  return (
    cardData?.layout &&
    FLIPPABLE_LAYOUTS.includes(cardData.layout) &&
    cardData.card_faces?.length >= 2 &&
    cardData.card_faces[0]?.image_uris &&
    cardData.card_faces[1]?.image_uris
  );
}

/**
 * Zoom in on a card. The actual card DOM element gets position:fixed
 * and animates from its slot to viewport center.
 *
 * @param {Object} cardInfo - Card info object from allCardsData
 * @param {HTMLElement} cardElement - The .card-slot DOM element
 */
export function zoomIn(cardInfo, cardElement) {
  if (zoomState.active || !cardInfo || cardInfo.status !== 'found' || !cardInfo.cardData) return;

  const cardData = cardInfo.cardData;
  const slotRect = cardElement.getBoundingClientRect();

  // Determine DFC status and image URLs
  const dfc = isDfcCard(cardData);
  const frontUrl = getCardImageUrl(cardData, 'large');
  const backUrl = dfc ? getCardBackImageUrl(cardData, 'large') : null;

  if (!frontUrl) return;

  // Preload back face image
  if (backUrl) {
    const preload = new Image();
    preload.src = backUrl;
  }

  zoomState.cardInfo = cardInfo;
  zoomState.cardElement = cardElement;
  zoomState.slotRect = slotRect;
  zoomState.isDfc = dfc;
  zoomState.showingBack = false;
  zoomState.dfcRotation = 0;
  zoomState.frontImageUrl = frontUrl;
  zoomState.backImageUrl = backUrl;
  zoomState.isSideways = determineCardRotation(cardData);
  zoomState.isSidewaysBack = dfc ? determineBackFaceRotation(cardData) : false;
  zoomState.tiltEnabled = false;
  zoomState.isFlipping = false;
  zoomState.isAnimating = true;
  zoomState.active = true;

  // Prevent body scroll
  document.body.style.overflow = 'hidden';
}

/**
 * Zoom out — animate card back to its slot position, then clean up.
 */
export function zoomOut() {
  if (!zoomState.active || zoomState.closing) return;

  // Don't snap DFC state here — CardSlot animates the flip-back during zoom-out.
  // finalizeZoomOut() resets all DFC state after animation completes.
  zoomState.tiltEnabled = false;
  zoomState.isAnimating = true;
  zoomState.closing = true;

  // CardSlot watches `closing` and animates back to slot rect.
  // After animation completes, CardSlot calls finalizeZoomOut().
}

/**
 * Called after zoom-out animation completes to reset all state.
 */
export function finalizeZoomOut() {
  document.body.style.overflow = '';
  zoomState.active = false;
  zoomState.closing = false;
  zoomState.cardInfo = null;
  zoomState.cardElement = null;
  zoomState.slotRect = null;
  zoomState.isDfc = false;
  zoomState.showingBack = false;
  zoomState.dfcRotation = 0;
  zoomState.frontImageUrl = null;
  zoomState.backImageUrl = null;
  zoomState.isSideways = false;
  zoomState.isSidewaysBack = false;
  zoomState.tiltEnabled = false;
  zoomState.isFlipping = false;
  zoomState.isAnimating = false;
}

/**
 * Enable tilt after zoom-in animation completes.
 */
export function enableTilt() {
  zoomState.tiltEnabled = true;
  zoomState.isAnimating = false;
}

/**
 * Flip a double-faced card between front and back.
 * Rotation starts immediately (CSS transition animates it).
 * Image swaps at the halfway point when the card is edge-on.
 */
export function flipDfc() {
  if (!zoomState.isDfc || zoomState.isFlipping) return;

  zoomState.isFlipping = true;

  // Start rotation immediately — CSS transition drives the visual
  zoomState.dfcRotation = zoomState.dfcRotation === 0 ? 180 : 0;

  const dfcFlipMs = getDfcFlipDurationMs();

  // At halfway (card is edge-on at 90°), swap the displayed image
  setTimeout(() => {
    zoomState.showingBack = !zoomState.showingBack;
  }, dfcFlipMs / 2);

  // After full rotation completes, clear flipping flag
  setTimeout(() => {
    zoomState.isFlipping = false;
  }, dfcFlipMs);
}

// Export constants for use in components
export { getZoomDurationMs, getDfcFlipDurationMs, EASING_SMOOTH };
